import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useContent } from '../hooks/useContent';
import { useSite } from '../hooks/useSites';
import { useToast } from '../hooks/useToast';
import { useUnsavedChanges } from '../hooks/useUnsavedChanges';
import { useTemplateManifest } from '../hooks/useTemplateManifest';
import { useEditorPreview } from '../hooks/useEditorPreview';
import { getCachedMediaUrl } from '../api/mediaCache';
import { getSchema } from '../api/schemas';
import { getContentId } from '../types';
import { firstMissingRequiredField } from '../utils/validateRequired';
import FormField from '../components/FormField';
import HelpPopover from '../components/HelpPopover';
import HeroButtonPreview from '../components/HeroButtonPreview';
import { pageHelp } from '../data/helpContent';
import '../styles/editor.css';

/**
 * Singleton content editor for the Hero section.
 * If content exists, loads it for editing. Otherwise shows a create form.
 */
export default function HeroEditor() {
  const { siteId } = useParams<{ siteId: string }>();
  const { site } = useSite(siteId);
  const { items, loading, create, update } = useContent(siteId, 'hero');
  const { addToast } = useToast();
  const { filterFields } = useTemplateManifest(site?.templateId);
  const { sendUpdate, sendMediaUpdate } = useEditorPreview('/');

  const [formData, setFormData] = useState<Record<string, unknown>>({});
  const [originalData, setOriginalData] = useState<Record<string, unknown>>({});
  const [saving, setSaving] = useState(false);

  const schema = site ? getSchema(site.templateId) : undefined;
  const rawContentType = schema?.contentTypes.hero;
  const filteredContentType = rawContentType ? filterFields('hero', rawContentType) : undefined;
  const fields = filteredContentType?.fields || {};
  const existingItem = items[0]; // Singleton — at most one item

  const isDirty = JSON.stringify(formData) !== JSON.stringify(originalData);
  useUnsavedChanges(isDirty);

  useEffect(() => {
    if (existingItem) {
      const data = existingItem.data || {};
      setFormData(data);
      setOriginalData(data);
    }
  }, [existingItem]);

  const handleChange = (name: string, value: unknown) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    const imageFields = ['backgroundImage', 'profileImage', 'imageUrl', 'featuredImage', 'logo'];
    if (imageFields.includes(name) && typeof value === 'string' && value) {
      const url = getCachedMediaUrl(value) || value;
      sendMediaUpdate('hero', name, url);
    } else {
      sendUpdate('hero', name, value);
    }
  };

  const handleSave = async () => {
    if (!siteId) return;
    const missing = firstMissingRequiredField(fields, formData);
    if (missing) {
      addToast('error', `Please fill in "${missing}" before saving`);
      return;
    }
    setSaving(true);
    try {
      if (existingItem) {
        const contentId = getContentId(existingItem.contentKey);
        await update(contentId, formData);
      } else {
        await create(formData);
      }
      setOriginalData({ ...formData });
      addToast('success', 'Homepage banner saved');
    } catch (err) {
      addToast('error', err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="page">
        <div className="loading-state"><div className="spinner" /></div>
      </div>
    );
  }

  const help = pageHelp.hero;

  return (
    <div className="page">
      <div className="page-header">
        <h1>Homepage Banner</h1>
        <div className="page-header-actions">
          <HelpPopover title={help.title} description={help.description} tips={help.tips} />
          {isDirty && <span className="unsaved-indicator">Unsaved changes</span>}
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>

      <div className="editor-form">
        {Object.entries(fields).map(([name, fieldSchema]) => (
          <div key={name}>
            {name === 'ctaText' && <HeroButtonPreview />}
            <FormField
              name={name}
              schema={fieldSchema}
              value={formData[name]}
              onChange={handleChange}
              siteId={siteId!}
              cmsType="hero"
            />
          </div>
        ))}
      </div>
    </div>
  );
}
